import { useEffect, useMemo } from 'react';

import { astrazeneca_settings } from '~implementations/astrazeneca';
import {
  COLSBUSIDIO_TENANT_COLORS,
  colsubsidio_settings
} from '~implementations/colsubsidio';
import { coomevamp_settings } from '~implementations/coomevamp';
import { coosalud_settings } from '~implementations/coosalud';
import { fci_settings } from '~implementations/fci';
import { hic_settings } from '~implementations/hic';
import { horizonte_settings } from '~implementations/horizonte';
import {
  incans_settings,
  INCANS_TENANT_COLORS,
  INCANS_TENANT_IMAGES
} from '~implementations/incans';
import { khymei_settings, KHYMEI_TENANT_COLORS } from '~implementations/khymei';
import {
  oculus_settings,
  OCULUS_TENANT_COLORS,
  OCULUS_TENANT_IMAGES
} from '~implementations/oculus';
import { rimac_settings } from '~implementations/rimac';
import { salud_total_settings } from '~implementations/saludTotal';
import { sanitas_settings } from '~implementations/sanitas';
import { useLocalStorage } from '.';

type TenantColorMap = Record<string, ImplementationSettings['colors']>;

const allAppSettings: Record<AppImplementation, ImplementationSettings> = {
  astrazeneca: astrazeneca_settings,
  oculus: oculus_settings,
  colsubsidio: colsubsidio_settings,
  colsubsidioTest: colsubsidio_settings,
  coosalud: coosalud_settings,
  coosaludTest: coosalud_settings,
  coomevamp: coomevamp_settings,
  fci: fci_settings,
  fciTest: fci_settings,
  rimac: rimac_settings,
  rimacTest: rimac_settings,
  horizonte: horizonte_settings,
  incans: incans_settings,
  salud_total: salud_total_settings,
  salud_totalTest: salud_total_settings,
  sanitas: sanitas_settings,
  hic: hic_settings,
  khymei: khymei_settings,
  khymeiTest: khymei_settings,
  hicTest: hic_settings
};

const tenantColorOverrides: Partial<
  Record<AppImplementation, { colorMap: TenantColorMap; fallback: string }>
> = {
  colsubsidio: {
    colorMap: COLSBUSIDIO_TENANT_COLORS,
    fallback: 'colsubsidio'
  },
  khymei: { colorMap: KHYMEI_TENANT_COLORS, fallback: 'base' },
  oculus: { colorMap: OCULUS_TENANT_COLORS, fallback: 'base' },
  incans: { colorMap: INCANS_TENANT_COLORS, fallback: 'base' }
};

const tenantImageOverrides: Partial<
  Record<AppImplementation, { imageMap: TenantImageMap; fallback: string }>
> = {
  oculus: { imageMap: OCULUS_TENANT_IMAGES, fallback: 'base' },
  incans: { imageMap: INCANS_TENANT_IMAGES, fallback: 'base' }
};

type TenantImageMap = Record<string, ImplementationSettings['logos']>;
export type Logos = keyof ImplementationSettings['logos'];
export type Envs = keyof ImplementationSettings['envs'];

export interface AppImplementationConfig {
  name: AppImplementation;
  envs: Record<Envs, string>;
  logos: Record<Logos, string>;
  workUnits: WorkUnit[];
}

const implementation = import.meta.env.VITE_IMPLEMENTATION;
const appSettings = allAppSettings[implementation];

function resolveColors(
  tenantKey: string | undefined
): ImplementationSettings['colors'] {
  const override = tenantColorOverrides[implementation];

  if (!override) return appSettings.colors;

  const { colorMap, fallback } = override;
  if (tenantKey && tenantKey in colorMap) return colorMap[tenantKey];
  return colorMap[fallback];
}

function resolveLogos(
  tenantKey: string | undefined
): ImplementationSettings['logos'] {
  const override = tenantImageOverrides[implementation];
  if (!override) return appSettings.logos;
  const { imageMap, fallback } = override;
  if (tenantKey && tenantKey in imageMap) return imageMap[tenantKey];
  return imageMap[fallback];
}

function applyCssVariables(colors: ImplementationSettings['colors']) {
  Object.entries(colors).forEach(([property, value]) => {
    document.documentElement.style.setProperty(property, value);
  });
}

const useImplementation = () => {
  const { userData } = useLocalStorage();
  const tenantKey = userData?.tenantSelected?.database_name;

  const colors = useMemo(() => resolveColors(tenantKey), [tenantKey]);
  const logos = useMemo(() => resolveLogos(tenantKey), [tenantKey]);

  useEffect(() => {
    applyCssVariables(colors);
  }, [colors]);

  return {
    name: implementation,
    ...appSettings,
    logos
  };
};

export default useImplementation;
